import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../api/client";
import { useAuth } from "../auth/AuthContext";
import BarcodeScanInput from "../components/BarcodeScanInput";
import Pagination from "../components/Pagination";
import ProductSearchSelect from "../components/ProductSearchSelect";
import SaleDetailPanel from "../components/SaleDetailPanel";
import { formatCurrency } from "../utils/currency";
import { formatQuantity, quantityStep } from "../utils/quantity";

const PAYMENT_METHODS = ["cash", "card", "mobile_money", "other"];
const PAGE_SIZE = 20;

// Matches utils/quantity.js's FRACTIONAL_UNITS - a barcode identifies WHICH
// product, never a WEIGHT, so a kg/litre product can't be auto-added on
// scan the way a piece product can.
const FRACTIONAL_UNITS = new Set(["kg", "litre"]);

function mergeIntoCart(cartLines, product, qty) {
  const existing = cartLines.find((line) => line.product_id === product.id);
  if (existing) {
    return cartLines.map((line) =>
      line.product_id === product.id ? { ...line, quantity: line.quantity + qty } : line
    );
  }
  return [
    ...cartLines,
    {
      product_id: product.id,
      name: product.name,
      unit_price: product.selling_price,
      quantity: qty,
      unit_of_measure: product.unit_of_measure,
    },
  ];
}

export default function SalesPage() {
  const { activeShop } = useAuth();
  const canSell = ["org_admin", "manager", "cashier"].includes(activeShop?.role);
  const shopId = activeShop?.shop_id;

  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [cart, setCart] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [discountTotal, setDiscountTotal] = useState("");
  const [taxTotal, setTaxTotal] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loadingSales, setLoadingSales] = useState(false);
  const [viewingSaleId, setViewingSaleId] = useState(null);
  const [viewingSale, setViewingSale] = useState(null);
  const [page, setPage] = useState(1);
  const [totalSales, setTotalSales] = useState(0);
  const [scanError, setScanError] = useState("");
  const quantityInputRef = useRef(null);
  const barcodeInputRef = useRef(null);

  useEffect(() => {
    if (message) {
      const t = setTimeout(() => setMessage(""), 5000);
      return () => clearTimeout(t);
    }
  }, [message]);

  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setError(""), 5000);
      return () => clearTimeout(t);
    }
  }, [error]);

  const fetchProducts = useCallback(async () => {
    if (!shopId) return;
    try {
      const res = await api.get(`/shops/${shopId}/products`);
      setProducts(res.data);
    } catch (err) {
      setError("Failed to load products");
    }
  }, [shopId]);

  const fetchSales = useCallback(async () => {
    if (!shopId) return;
    setLoadingSales(true);
    try {
      const res = await api.get(`/shops/${shopId}/sales`, { params: { page, page_size: PAGE_SIZE } });
      setSales(res.data.items);
      setTotalSales(res.data.total);
    } catch (err) {
      setError("Failed to load sales history");
    }
    setLoadingSales(false);
  }, [shopId, page]);

  useEffect(() => {
    setCart([]);
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    fetchSales();
  }, [fetchSales]);

  const productsById = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);

  const addToCart = (e) => {
    e.preventDefault();
    const product = productsById[Number(selectedProductId)];
    if (!product) {
      setError("Please select a product");
      return;
    }
    const qty = Number(quantity || 1);
    if (qty <= 0) return;

    setCart((prev) => mergeIntoCart(prev, product, qty));
    setQuantity("1");
    // Return focus to the scan field so the cashier's next scan doesn't end
    // up typed into whatever field last had focus (e.g. the "Add to cart"
    // button, or the quantity input they just edited).
    barcodeInputRef.current?.focus();
  };

  const handleBarcodeScan = async (barcode) => {
    setScanError("");
    setError("");
    try {
      const res = await api.get(`/shops/${shopId}/products/lookup-barcode/${encodeURIComponent(barcode)}`);
      const product = res.data;

      if (product.quantity_on_hand <= 0) {
        setScanError(`${product.name} is out of stock`);
        return;
      }

      // A scan only identifies WHICH product, never how many were bought (a
      // piece product might be bought 3-at-a-time, and a barcode obviously
      // can't tell you a weight at all) - so select it and default the
      // quantity rather than committing straight to the cart, same as
      // picking it from the dropdown. Piece products default to 1 (the
      // common case, still editable); kg/litre default to blank since
      // there's no sensible default weight to guess.
      setSelectedProductId(String(product.id));
      setQuantity(FRACTIONAL_UNITS.has(product.unit_of_measure) ? "" : "1");
      quantityInputRef.current?.focus();
      quantityInputRef.current?.select();
    } catch (err) {
      if (err.response?.status === 404) {
        setScanError(`No product found for barcode "${barcode}"`);
      } else {
        setScanError("Barcode lookup failed");
      }
    }
  };

  const removeFromCart = (productId) => {
    setCart((prev) => prev.filter((line) => line.product_id !== productId));
  };

  const cartSubtotal = useMemo(
    () => cart.reduce((sum, line) => sum + line.unit_price * line.quantity, 0),
    [cart]
  );

  const resetSaleForm = () => {
    setCart([]);
    setDiscountTotal("");
    setTaxTotal("");
    setPaymentMethod("cash");
  };

  const completeSale = async () => {
    if (cart.length === 0) return;
    setSubmitting(true);
    setMessage("");
    setError("");
    try {
      const res = await api.post(`/shops/${shopId}/sales`, {
        payment_method: paymentMethod,
        discount_total: Number(discountTotal || 0),
        tax_total: Number(taxTotal || 0),
        line_items: cart.map((line) => ({ product_id: line.product_id, quantity: line.quantity })),
      });
      setMessage(`Sale ${res.data.receipt_no} completed - total ${formatCurrency(res.data.total_amount, activeShop?.currency)}`);
      resetSaleForm();
      fetchProducts();
      if (page === 1) {
        fetchSales();
      } else {
        setPage(1);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to complete sale");
    }
    setSubmitting(false);
  };

  const openSale = async (id) => {
    setViewingSaleId(id);
    try {
      const res = await api.get(`/shops/${shopId}/sales/${id}`);
      setViewingSale(res.data);
    } catch (err) {
      setError("Failed to load sale");
      setViewingSaleId(null);
    }
  };

  const currency = (n) => formatCurrency(n, activeShop?.currency);

  if (!activeShop) {
    return <div className="card">No shop assigned to your account yet.</div>;
  }

  if (viewingSaleId && viewingSale) {
    return (
      <SaleDetailPanel
        sale={viewingSale}
        onClose={() => {
          setViewingSaleId(null);
          setViewingSale(null);
        }}
      />
    );
  }

  return (
    <>
      <div className="content-grid">
        {canSell && (
          <div className="card form-card">
            <h2>New Sale</h2>
            <BarcodeScanInput
              ref={barcodeInputRef}
              onScan={handleBarcodeScan}
              placeholder="Scan item barcode..."
              autoFocus
            />
            {scanError && <div className="error-msg">{scanError}</div>}
            <form onSubmit={addToCart} className="product-form">
              <ProductSearchSelect
                products={products}
                value={selectedProductId}
                onChange={setSelectedProductId}
                currency={currency}
              />
              <input
                ref={quantityInputRef}
                type="number"
                min={quantityStep(productsById[Number(selectedProductId)]?.unit_of_measure)}
                step={quantityStep(productsById[Number(selectedProductId)]?.unit_of_measure)}
                placeholder="Quantity"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                required
              />
              <button className="btn" type="submit">
                Add to cart
              </button>
            </form>

            {cart.length > 0 && (
              <div className="scroll-x" style={{ marginTop: 16 }}>
                <table className="product-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Qty</th>
                      <th>Price</th>
                      <th>Total</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {cart.map((line) => (
                      <tr key={line.product_id}>
                        <td className="name-cell">{line.name}</td>
                        <td>{formatQuantity(line.quantity, line.unit_of_measure)}</td>
                        <td>{currency(line.unit_price)}</td>
                        <td>{currency(line.unit_price * line.quantity)}</td>
                        <td>
                          <button className="btn btn-delete" type="button" onClick={() => removeFromCart(line.product_id)}>
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="stock-form" style={{ marginTop: 16 }}>
              <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                {PAYMENT_METHODS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
              <input
                type="number"
                placeholder="Discount"
                value={discountTotal}
                onChange={(e) => setDiscountTotal(e.target.value)}
                step="0.01"
                min="0"
              />
              <input
                type="number"
                placeholder="Tax"
                value={taxTotal}
                onChange={(e) => setTaxTotal(e.target.value)}
                step="0.01"
                min="0"
              />
            </div>

            <div className="sale-totals">
              <div className="sale-total-final">Subtotal: {currency(cartSubtotal)}</div>
            </div>

            <div className="form-actions" style={{ marginTop: 12 }}>
              <button className="btn" onClick={completeSale} disabled={submitting || cart.length === 0}>
                Complete Sale
              </button>
            </div>

            {message && <div className="success-msg">{message}</div>}
            {error && <div className="error-msg">{error}</div>}
          </div>
        )}

        <div className="card list-card">
          <h2>Sales History</h2>
          {!canSell && (message || error) && (
            <>
              {message && <div className="success-msg">{message}</div>}
              {error && <div className="error-msg">{error}</div>}
            </>
          )}
          {loadingSales ? (
            <div className="loader">Loading...</div>
          ) : (
            <>
            <div className="scroll-x">
              <table className="product-table">
                <thead>
                  <tr>
                    <th>Receipt</th>
                    <th>Date</th>
                    <th>Payment</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((s) => (
                    <tr key={s.id}>
                      <td>{s.receipt_no}</td>
                      <td>{new Date(s.created_at).toLocaleString()}</td>
                      <td>{s.payment_method}</td>
                      <td className="price-cell">{currency(s.total_amount)}</td>
                      <td>{s.status}</td>
                      <td>
                        <button className="btn btn-edit" onClick={() => openSale(s.id)}>
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                  {sales.length === 0 && (
                    <tr>
                      <td colSpan={6} className="empty">
                        No sales yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={Math.max(1, Math.ceil(totalSales / PAGE_SIZE))} onPageChange={setPage} />
            </>
          )}
        </div>
      </div>
    </>
  );
}
